import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FaArrowLeft } from "react-icons/fa";
import { createOfficeStaff, updateOfficeStaff, fetchOfficeStaffById } from "./supportContactService";
import "../assets/css/AddSupportContact.css";

/**
 * AddOfficeStaff
 *
 * Form to create or edit office staff.
 * Integrates with POST /create-office-staff and PUT /office-staff/:id APIs.
 */
function AddOfficeStaff({ onBack, onAddStaff }) {
  const navigate = useNavigate();
  const { id: staffId } = useParams();
  const isEdit = Boolean(staffId);
  const [loading, setLoading] = useState(isEdit);
  const [formData, setFormData] = useState({
    staffName: "",
    staffRole: "",
    phoneNumber: "",
    alternatePhone: "",
    email: "",
    shiftStart: "",
    shiftEnd: "",
    workingDays: "",
    supervisorName: "",
    remarks: "",
    status: "Active"
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setError("");
  };

  const getUserId = () => {
    try {
      const userStr = localStorage.getItem("user");
      if (userStr) {
        const user = JSON.parse(userStr);
        return user.user_id ?? user.id ?? 1;
      }
    } catch (err) {
      console.error("Error reading user:", err);
    }
    return 1;
  };

  useEffect(() => {
    if (!isEdit || !staffId) return;
    const load = async () => {
      setLoading(true);
      try {
        const s = await fetchOfficeStaffById(staffId);
        if (s) {
          setFormData({
            staffName: s.Staff_Name ?? s.staff_name ?? "",
            staffRole: s.Staff_Role ?? s.staff_role ?? "",
            phoneNumber: s.Phone_Number ?? s.phone_number ?? "",
            alternatePhone: s.Alternate_Phone ?? s.alternate_phone ?? "",
            email: s.Email ?? s.email ?? "",
            shiftStart: s.Shift_Start ?? s.shift_start ?? "",
            shiftEnd: s.Shift_End ?? s.shift_end ?? "",
            workingDays: s.Working_Days ?? s.working_days ?? "",
            supervisorName: s.Supervisor_Name ?? s.supervisor_name ?? "",
            remarks: s.Remarks ?? s.remarks ?? "",
            status: s.Status ?? s.status ?? "Active"
          });
        } else {
          alert("Staff not found.");
        }
      } catch (err) {
        console.error("Error fetching office staff:", err);
        alert("Failed to load staff.");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [isEdit, staffId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!formData.staffName?.trim()) {
      setError("Staff Name is required.");
      return;
    }
    if (!formData.staffRole?.trim()) {
      setError("Staff Role is required.");
      return;
    }
    if (!formData.phoneNumber?.trim()) {
      setError("Phone Number is required.");
      return;
    }

    const payload = {
      Staff_Name: formData.staffName.trim(),
      Staff_Role: formData.staffRole.trim() || null,
      Phone_Number: formData.phoneNumber.trim() || null,
      Alternate_Phone: formData.alternatePhone.trim() || null,
      Email: formData.email.trim() || null,
      Shift_Start: formData.shiftStart.trim() || null,
      Shift_End: formData.shiftEnd.trim() || null,
      Working_Days: formData.workingDays.trim() || null,
      Supervisor_Name: formData.supervisorName.trim() || null,
      Remarks: formData.remarks.trim() || null,
      Status: formData.status || "Active",
      Updated_By: getUserId()
    };

    setSubmitting(true);
    try {
      if (isEdit) {
        await updateOfficeStaff(staffId, payload);
        alert("Office staff updated successfully.");
      } else {
        payload.Created_By = getUserId();
        await createOfficeStaff(payload);
        alert("Office staff created successfully.");
      }
      if (onAddStaff) onAddStaff();
      if (onBack) onBack();
      else navigate("/support-contacts/staff");
    } catch (err) {
      console.error("Error creating office staff:", err);
      const msg =
        err.response?.data?.message ||
        err.response?.data?.error ||
        err.message ||
        "Failed to create office staff.";
      setError(msg);
      alert(msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="addsupportcontact-container">
        <div className="addsupportcontact-loading">Loading staff...</div>
      </div>
    );
  }

  return (
    <div className="addsupportcontact-container">
      <div className="addsupportcontact-header">
        <div className="addsupportcontact-title-section">
          <button
            type="button"
            className="addsupportcontact-back-link"
            onClick={() => (onBack ? onBack() : navigate("/support-contacts/staff"))}
          >
            <FaArrowLeft /> Back to Support Staff
          </button>
        </div>
      </div>

      <div className="addsupportcontact-form-card">
        <form className="addsupportcontact-form" onSubmit={handleSubmit}>
          {error && (
            <div className="addsupportcontact-error" role="alert">
              {error}
            </div>
          )}
          <div className="addofficeservice-form-grid">
            <div className="addsupportcontact-form-group">
              <label>
                Staff Name <span className="required">*</span>
              </label>
              <input
                type="text"
                name="staffName"
                value={formData.staffName}
                onChange={handleInputChange}
                required
              />
            </div>
            <div className="addsupportcontact-form-group">
              <label>
                Staff Role <span className="required">*</span>
              </label>
              <input
                type="text"
                name="staffRole"
                value={formData.staffRole}
                onChange={handleInputChange}
                required
              />
            </div>
            <div className="addsupportcontact-form-group">
              <label>
                Phone Number <span className="required">*</span>
              </label>
              <input
                type="text"
                name="phoneNumber"
                value={formData.phoneNumber}
                onChange={handleInputChange}
                required
              />
            </div>
            <div className="addsupportcontact-form-group">
              <label>Alternate Phone</label>
              <input
                type="text"
                name="alternatePhone"
                value={formData.alternatePhone}
                onChange={handleInputChange}
              />
            </div>
            <div className="addsupportcontact-form-group">
              <label>Email</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
              />
            </div>
            <div className="addsupportcontact-form-group">
              <label>Shift Start</label>
              <input
                type="text"
                name="shiftStart"
                value={formData.shiftStart}
                onChange={handleInputChange}
              />
            </div>
            <div className="addsupportcontact-form-group">
              <label>Shift End</label>
              <input
                type="text"
                name="shiftEnd"
                value={formData.shiftEnd}
                onChange={handleInputChange}
              />
            </div>
            <div className="addsupportcontact-form-group">
              <label>Working Days</label>
              <input
                type="text"
                name="workingDays"
                value={formData.workingDays}
                onChange={handleInputChange}
              />
            </div>
            <div className="addsupportcontact-form-group">
              <label>Supervisor Name</label>
              <input
                type="text"
                name="supervisorName"
                value={formData.supervisorName}
                onChange={handleInputChange}
              />
            </div>
            <div className="addsupportcontact-form-group">
              <label>Status</label>
              <select
                name="status"
                value={formData.status}
                onChange={handleInputChange}
              >
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
              </select>
            </div>
            <div className="addsupportcontact-form-group addofficeservice-notes-span">
              <label>Remarks</label>
              <textarea
                name="remarks"
                value={formData.remarks}
                onChange={handleInputChange}
                rows={4}
              />
            </div>
          </div>
          <div className="addsupportcontact-form-actions addsupportcontact-form-actions-no-border">
            <button type="submit" className="btn-primary btn-add-lead" disabled={submitting}>
              {submitting ? (isEdit ? "Updating..." : "Creating...") : (isEdit ? "Update Staff" : "Add Staff")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default AddOfficeStaff;
